import http from "@/libs/http";
import type { Product } from "@/types/product";
import { CSSP_OWN_STOCK_SUPPLIER_CODE } from "./constants";
import { mapCsspBrands, mapCsspSupplierResponse, mergeCsspResults } from "./csspMappers";
import { CsspBrand, CsspOffersResult, CsspSearchParams, CsspSupplier, CsspSupplierCode } from "./types";

type RawRecord = Record<string, unknown>;

export class CsspSupplierSearchError extends Error {
  constructor(readonly failedSupplierCount: number) {
    super("All CSSP supplier requests failed");
  }
}

const asRecord = (value: unknown): RawRecord | null =>
  value && typeof value === "object" && !Array.isArray(value) ? value as RawRecord : null;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

/** Strict boundary for the externally-owned by-city payload. */
export const mapCsspSuppliers = (payload: unknown): CsspSupplier[] => {
  const root = asRecord(payload);
  if (!root || root.success !== true || !Array.isArray(root.suppliers)) {
    throw new Error("Invalid CSSP supplier availability response");
  }

  const seen = new Set<string>();
  const mapped: CsspSupplier[] = [];
  for (const value of root.suppliers) {
    const supplier = asRecord(value);
    if (!supplier ||
      !isNonEmptyString(supplier.code) ||
      !isNonEmptyString(supplier.name) ||
      !isNonEmptyString(supplier.short_name) ||
      !Array.isArray(supplier.methods) ||
      !supplier.methods.every((method) => typeof method === "string") ||
      typeof supplier.is_active !== "boolean") {
      throw new Error("Invalid CSSP supplier availability record");
    }

    if (!supplier.is_active || !supplier.methods.includes("searchDetail") || seen.has(supplier.code)) continue;
    seen.add(supplier.code);
    mapped.push({
      code: supplier.code,
      name: supplier.name,
      shortName: supplier.short_name,
    });
  }
  return mapped;
};

export const fetchCsspSuppliers = async (cityId: string, signal?: AbortSignal): Promise<CsspSupplier[]> => {
  const { data } = await http.get("/api/c-suppliers/by-city", {
    params: { city_id: cityId },
    signal,
  });
  return mapCsspSuppliers(data);
};

export const fetchCsspBrands = async (artikul: string, signal?: AbortSignal): Promise<CsspBrand[]> => {
  const { data } = await http.get("/api/suppliers/brands", {
    params: { artikul },
    signal,
  });

  return mapCsspBrands(data);
};

export const fetchCsspSupplierOffers = async (
  params: CsspSearchParams,
  supplier: CsspSupplierCode,
  supplierIndex: number,
  signal?: AbortSignal
) => {
  const response = await http.get("/api/c-suppliers/search-detail", {
    params: {
      city_id: params.cityId,
      supplier,
      artikul: params.artikul,
      brand: params.brand,
      useAnalogs: params.useAnalogs ? 1 : 0,
    },
    signal,
  });
  return mapCsspSupplierResponse(response.data, params, supplier, supplierIndex);
};

export const fetchCsspExternalOffers = async (
  params: CsspSearchParams,
  suppliers: readonly CsspSupplier[],
  signal?: AbortSignal
): Promise<CsspOffersResult> => {
  const uniqueSuppliers = suppliers.filter((supplier, index, all) =>
    supplier.code !== CSSP_OWN_STOCK_SUPPLIER_CODE && all.findIndex((item) => item.code === supplier.code) === index
  );
  if (uniqueSuppliers.length === 0) return mergeCsspResults([], 0);
  const requests = uniqueSuppliers.map((supplier, supplierIndex) =>
    fetchCsspSupplierOffers(params, supplier.code, supplierIndex + 1, signal)
  );

  const settled = await Promise.allSettled(requests);
  const successful = settled
    .filter((result): result is PromiseFulfilledResult<Awaited<(typeof requests)[number]>> =>
      result.status === "fulfilled"
    )
    .map((result) => result.value);

  if (successful.length === 0) {
    throw new CsspSupplierSearchError(settled.length);
  }

  return mergeCsspResults(successful, settled.length - successful.length);
};

/** Complete standalone search: own stock starts before the API-authorized fan-out. */
export const fetchCsspOffers = async (
  params: CsspSearchParams,
  suppliers: readonly CsspSupplier[],
  signal?: AbortSignal
): Promise<CsspOffersResult> => {
  const ownStockRequest = fetchCsspSupplierOffers(params, CSSP_OWN_STOCK_SUPPLIER_CODE, 0, signal);
  const externalRequest = fetchCsspExternalOffers(params, suppliers, signal);
  const [ownStock, external] = await Promise.allSettled([ownStockRequest, externalRequest]);
  const successful = [ownStock, external]
    .filter((result): result is PromiseFulfilledResult<Awaited<typeof ownStockRequest> | CsspOffersResult> => result.status === "fulfilled")
    .map((result) => result.value);
  if (successful.length === 0) throw new CsspSupplierSearchError(2);

  const mapped = successful.flatMap((result) => "failedSupplierCount" in result
    ? [result]
    : [mergeCsspResults([result], 0)]
  );
  const merged = mergeCsspResults(
    mapped.flatMap((result) => [{ offers: result.offers, inStock: result.inStock }]),
    mapped.reduce((total, result) => total + result.failedSupplierCount, 0) + (2 - successful.length)
  );
  return {
    ...merged,
    successSupplierCount: successful.reduce(
      (total, result) => total + ("successSupplierCount" in result ? result.successSupplierCount : 1),
      0
    ),
  };
};

export const fetchCsspProductsByRefKeys = async (refKeys: string[], signal?: AbortSignal): Promise<Product[]> => {
  const uniqueRefKeys = Array.from(new Set(refKeys.map((refKey) => refKey.trim()).filter(Boolean)));
  const settled = await Promise.allSettled(
    uniqueRefKeys.map((refKey) => http.get<Product>(`api/products/get/${refKey}`, { signal }))
  );

  return settled.flatMap((result) => (result.status === "fulfilled" ? [result.value.data] : []));
};
